<script setup lang="ts">
import ButtonLoader from './button-loader/ButtonLoader.vue';

const props = defineProps({
  mode: {
    type: String,
    default: 'button',
    validator: (value: string) => ['button', 'link', 'div'].includes(value),
  },
  type: {
    type: String,
    default: 'primary',
    validator: (value: string) =>
      ['primary', 'secondary', 'neutral', 'tertiary', 'outline'].includes(
        value
      ),
  },
  size: {
    type: String,
    default: 'medium',
    validator: (value: string) => ['large', 'medium'].includes(value),
  },
  sizeIco: {
    type: String,
    validator: (value: string) => ['16', '20', '24', '28'].includes(value),
  },
  iconBefore: {
    type: String,
    default: '',
  },
  counter: {
    type: Number,
    default: 0,
  },
  iconAfter: {
    type: String,
    default: '',
  },
  fontClass: {
    type: String,
    default: 'body-l-regular',
    validator: (value: string) =>
      [
        'body-l-medium',
        'body-m-medium',
        'body-l-regular',
        'body-m-regular',
      ].includes(value),
  },
  minWidth: {
    type: Number,
    default: 0,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  resize: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  responsiveIco: {
    type: Boolean,
    default: true,
  },
  loadingType: {
    type: String,
    default: 'grey',
  },
  path: {
    type: [String, Object],
  },
  fill: {
    type: String,
    default: '',
  },
  stroke: {
    type: String,
    default: '',
  },
  hoverFill: {
    type: String,
    default: '',
  },
  hoverStroke: {
    type: String,
    default: '',
  },
});
const emit = defineEmits(['click']);
const clickButtons = (e: Event): void => {
  if (props.mode !== 'div') {
    e.preventDefault();
  }
  if (!props.disabled) {
    emit('click', e);
  }
};
const viewComponent = computed(() =>
  props.mode === 'link' ? defineNuxtLink({}) : props.mode
);
const colorButton = (item: string) => {
  switch (item) {
    case 'primary':
      return '#FFFFFF';
    case 'secondary':
      return '#363EF8';
    case 'neutral':
      return '#212121';
    case 'tertiary':
      return '#363EF8';
    case 'outline':
      return '#363EF8';
  }
};
</script>

<template>
  <component
    :is="viewComponent"
    :class="[
      'button-wrapper-encapsulation ',
      type,
      size,
      `${type}__${size}`,
      { button__disabled: disabled },
      `${type}__border`,
      loading ? 'l-min-width' : '',
    ]"
    :style="[
      Number(minWidth) > 0 ? `minWidth: ${minWidth}px` : '',
      resize ? `width: 100% ` : '',
    ]"
    :disabled="disabled || loading"
    :to="path"
    @click="clickButtons"
  >
    <div v-if="!!iconBefore && !loading" class="button__ico-before">
      <UiIcon
        :name="iconBefore"
        :fill="fill"
        :stroke="stroke || colorButton(type)"
        :hover-fill="hoverFill"
        :hover-stroke="hoverStroke"
        :size="sizeIco"
        :response-size="responsiveIco ? [16, 16, 16, 20, 20, 20] : []"
      />
    </div>
    <ButtonLoader v-if="loading" :type="loadingType" />
    <div v-else :class="['button-text', fontClass]">
      <slot />
    </div>
    <div
      v-if="!!counter && !loading"
      :class="['button-text', 'button__counter', fontClass]"
    >
      {{ counter }}
    </div>
    <div v-if="!!iconAfter && !loading" class="button__ico-after">
      <UiIcon
        :name="iconAfter"
        :fill="fill"
        :stroke="stroke || colorButton(type)"
        :hover-fill="hoverFill"
        :hover-stroke="hoverStroke"
        :size="sizeIco"
        :response-size="responsiveIco ? [16, 16, 16, 20, 20, 20] : []"
      />
    </div>
  </component>
</template>

<style scoped lang="scss" src="./ui-button-css.scss" />
